/**
 * <sarky-animated-search>
 *
 * Wraps a real <input type="search"> and types/deletes a rotation of
 * suffixes after a fixed prefix, shown as a separate, decorative,
 * pointer-events:none text overlay layered on top of the input - never
 * by mutating `placeholder` and never by touching the input's real
 * `value`, so it can never interfere with what a shopper actually types
 * or submits.
 *
 * Usage:
 *   <sarky-animated-search data-prefix="SEARCH " data-phrases="DRESSES…|NEW IN…">
 *     <form>...<input type="search">...</form>
 *   </sarky-animated-search>
 *
 * The host page must give the input's form/wrapper `position: relative`
 * and add a `.sarky-animated-search__overlay` rule positioning the
 * overlay to match that input's own text position (see
 * snippets/sarky-header-search.liquid and snippets/header-drawer.liquid
 * for the two real examples) - this file only creates the element and
 * drives its text/visibility.
 *
 * - `data-prefix` is always shown and is never typed/deleted - only the
 *   text after it (from `data-phrases`) animates. The animation never
 *   goes back further than the bare prefix. Stored as `this.searchPrefix`
 *   (NOT `this.prefix`) - `prefix` is a built-in, getter-only property on
 *   every DOM Element (its XML namespace prefix); assigning to
 *   `this.prefix` throws `TypeError: Cannot set property prefix of
 *   #<Element> which has only a getter` in every real browser, which
 *   silently killed connectedCallback (and therefore the whole animation)
 *   before it ever reached the timer loop - the actual root cause behind
 *   every prior "typing not visible" report, confirmed via a real Chrome
 *   DevTools stack trace. A hand-rolled Node.js DOM test double does not
 *   have this Element.prototype.prefix accessor, which is why earlier
 *   harness-based verification passed despite the bug being real.
 * - The overlay is hidden the moment the input has a real value (typed
 *   or otherwise), regardless of focus - exactly like a native
 *   placeholder disappears once you start typing - and reappears once
 *   the input is emptied again.
 * - Respects prefers-reduced-motion: no overlay is created at all: the
 *   real input's own `placeholder` attribute gets the full static text
 *   instead, so there is nothing to disable/force past.
 * - Pauses purely on the input having a real VALUE, never on focus alone
 *   - a focused-but-empty field keeps animating underneath (a host page
 *   may still choose to visually hide the overlay while focused via its
 *   own CSS, e.g. snippets/search-modal.liquid; that is a presentation
 *   choice independent of this timer). Resumes the instant the value
 *   empties again, whether that happens while focused (typed text
 *   cleared) or on blur.
 * - If this element sits inside a <dialog-component> (assets/dialog.js),
 *   it listens for that dialog's own 'dialog:open' event and forces an
 *   immediate tick the moment it fires, clearing any pending poll-retry
 *   first. Without this, a component that connected while its dialog was
 *   still closed - the normal case, since the modal's markup exists in
 *   the DOM from page load - only ever finds out it became visible via
 *   the tick() poll below, which can take up to 400ms and, critically,
 *   never even started counting characters while hidden (tick() always
 *   exits before reaching the typing logic while offsetParent is null),
 *   so the overlay could sit showing only the bare, un-animated prefix
 *   for that whole window right after the dialog opens - this was the
 *   real cause of the search overlay looking "blank" on first open.
 *   Harmless no-op for every other host (header bar, drawer), which have
 *   no <dialog-component> ancestor.
 * - No layout shift: the overlay is `position: absolute`, out of flow;
 *   the input's own box size is controlled entirely by CSS.
 * - No animation library - a single small timer loop.
 * - Re-acquires its <input> reference defensively if the original node is
 *   ever detached from the document, and skips the DOM write (but keeps
 *   the loop alive) while hidden via CSS (see tick()).
 * - Rebuilds its overlay <span> (and re-clears the input's placeholder)
 *   if it's ever found missing or detached - Shopify's own section
 *   hydration (assets/section-hydration.js) can morph the drawer's DOM
 *   back toward its server-rendered Liquid HTML shortly after page load,
 *   which has no knowledge of this JS-created element and can silently
 *   remove it while leaving this custom element itself connected (see
 *   tick()) - this was the confirmed cause of the drawer instance
 *   getting permanently stuck showing the static "SEARCH" placeholder
 *   with no console error.
 */
class SarkyAnimatedSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"], input[type="text"]');
    if (!this.input) return;

    this.searchPrefix = this.getAttribute('data-prefix') || '';

    const phrasesAttr = this.getAttribute('data-phrases') || '';
    this.phrases = phrasesAttr
      .split('|')
      .map((phrase) => phrase.trim())
      .filter(Boolean);

    this.staticText =
      this.getAttribute('data-static-text') ||
      this.getAttribute('data-static-placeholder') ||
      this.input.placeholder ||
      this.searchPrefix ||
      'Search';

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || this.phrases.length === 0) {
      this.input.placeholder = this.staticText;
      return;
    }

    this.overlay = document.createElement('span');
    this.overlay.className = 'sarky-animated-search__overlay';
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.textContent = this.searchPrefix;
    this.input.insertAdjacentElement('afterend', this.overlay);

    // The overlay renders all visible text now - an empty native
    // placeholder here (not the static fallback text) avoids the two
    // rendering on top of each other at slightly different positions.
    this.input.placeholder = '';

    this.phraseIndex = 0;
    this.charIndex = 0;
    this.deleting = false;
    this.timerId = null;

    this.handleBlur = () => {
      // Each focus needs its own fresh proof of genuine engagement - see
      // handleFocus below (dialog-hosted instances only).
      this.hasEngagementIntent = false;
      this.syncOverlayVisibility();
      if (!this.input.value) this.resume();
    };
    // Always re-enters tick() itself (never a bare pause()) so a value
    // stays covered by tick()'s own lightweight recheck loop - see the
    // comment on that branch for why a direct pause() here would be a
    // dead end that nothing ever wakes back up.
    this.handleInput = () => {
      this.syncOverlayVisibility();
      this.pause();
      this.tick();
    };

    this.input.addEventListener('blur', this.handleBlur);
    this.input.addEventListener('input', this.handleInput);

    // See the class doc comment above for why this exists.
    this.dialogHost = this.closest('dialog-component');
    if (this.dialogHost) {
      // Billy's explicit, literal rule: the animated placeholder is the
      // default, idle state any time this field isn't actually being
      // used - including the very first frame after entering search -
      // and it may ONLY disappear once a shopper genuinely taps/clicks
      // the bar themselves. A plain CSS `:has(.search-input:focus)` rule
      // used to drive the hide (see snippets/search-modal.liquid), but a
      // `:focus` match can't tell a real tap apart from the native
      // <dialog>.showModal() auto-focus (or any other host-driven
      // programmatic focus - confirmed happening on real devices even
      // though it doesn't reproduce under either Chromium or WebKit
      // automation here), which silently hid the overlay on open with
      // nothing left to undo it. This makes the hide-on-focus decision
      // entirely JS-driven and timing-independent instead of guessing at
      // exactly when the dialog's own auto-focus happens:
      // `hasEngagementIntent` is only ever set true by a real pointerdown
      // landing on this exact input, or a Tab keydown, immediately
      // beforehand - a genuine tap, click, or keyboard Tab-navigation
      // always fires one of those before its resulting focus event, but
      // a purely programmatic `.focus()` call never does. Any focus
      // event that arrives without either is rejected outright (blurred
      // straight back).
      //
      // Scoped to dialog-hosted instances only (this search modal - the
      // only place the native auto-focus-on-open risk actually exists).
      // The header bar/drawer's own decorative, non-dialog instances and
      // the standalone /search page's real input (blocks/_search-input.liquid)
      // keep their original, simpler value-only hide behavior untouched.
      this.hasEngagementIntent = false;
      this.handlePointerDown = () => {
        this.hasEngagementIntent = true;
      };
      this.handleTabKeyDown = (event) => {
        if (event.key === 'Tab') this.hasEngagementIntent = true;
      };
      this.handleFocus = () => {
        if (!this.hasEngagementIntent) {
          this.input.blur();
          return;
        }
        this.syncOverlayVisibility();
      };

      this.input.addEventListener('focus', this.handleFocus);
      this.input.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
      document.addEventListener('keydown', this.handleTabKeyDown, { capture: true });

      this.handleDialogOpen = () => {
        // Clears out any stale intent left over from an abandoned touch
        // (pointerdown without a completed focus) in a previous session,
        // so a fresh dialog open always starts from a clean slate.
        this.hasEngagementIntent = false;
        this.pause();
        this.tick();
      };
      this.dialogHost.addEventListener('dialog:open', this.handleDialogOpen);
    }

    this.syncOverlayVisibility();
    this.tick();
  }

  disconnectedCallback() {
    this.pause();
    if (this.input) {
      this.input.removeEventListener('blur', this.handleBlur);
      this.input.removeEventListener('input', this.handleInput);
      if (this.handleFocus) this.input.removeEventListener('focus', this.handleFocus);
      if (this.handlePointerDown) this.input.removeEventListener('pointerdown', this.handlePointerDown);
    }
    if (this.handleTabKeyDown) {
      document.removeEventListener('keydown', this.handleTabKeyDown, { capture: true });
    }
    if (this.dialogHost) {
      this.dialogHost.removeEventListener('dialog:open', this.handleDialogOpen);
      this.dialogHost = null;
    }
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  pause() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  resume() {
    if (!this.timerId) this.tick();
  }

  syncOverlayVisibility() {
    if (!this.overlay) return;
    // Hides on a real typed value (unrelated to focus - e.g. an
    // external caller like predictive-search.js's reset button clearing
    // the field programmatically) OR on a focus event this component
    // already verified was genuine (see handleFocus/hasEngagementIntent
    // above) - never on focus alone.
    const isGenuinelyFocused = Boolean(this.hasEngagementIntent) && document.activeElement === this.input;
    this.overlay.hidden = Boolean(this.input.value) || isGenuinelyFocused;
  }

  tick() {
    // Defensive re-acquire: if this element's light-DOM was replaced by an
    // external re-render after connectedCallback first ran, `this.input`
    // could otherwise point at a detached node.
    if (!this.input.isConnected) {
      const freshInput = this.querySelector('input[type="search"], input[type="text"]');
      if (freshInput) {
        this.input = freshInput;
      } else {
        this.pause();
        return;
      }
    }

    // Shopify's own section-hydration (assets/section-hydration.js ->
    // assets/section-renderer.js -> @theme/morph) re-fetches and morphs
    // the drawer's markup shortly after page load to "hydrate" it. That
    // morph patches the live DOM to match freshly server-rendered Liquid
    // HTML - which has no knowledge of the overlay <span> this component
    // creates itself (it only exists because connectedCallback made it,
    // it is not in any Liquid template). Because the morph reuses/keeps
    // this custom element in place rather than destroying and recreating
    // it, connectedCallback never re-runs to notice - but the morph can
    // still remove the now-untracked overlay node from the DOM, and can
    // restore the input's static `placeholder="SEARCH"` HTML attribute
    // over our earlier `this.input.placeholder = ''`. The net effect:
    // this.overlay silently becomes a detached node that tick() keeps
    // updating with nobody able to see it, while the real, visible input
    // just shows its static "SEARCH" placeholder forever - with no
    // console error at any point, since writing to a detached node's
    // textContent is not an error. Rebuild the overlay (and re-clear the
    // placeholder) whenever it's found missing or detached, so the
    // animation keeps reflecting into whatever is actually on screen.
    if (!this.overlay || !this.overlay.isConnected) {
      this.overlay = document.createElement('span');
      this.overlay.className = 'sarky-animated-search__overlay';
      this.overlay.setAttribute('aria-hidden', 'true');
      this.input.insertAdjacentElement('afterend', this.overlay);
      this.input.placeholder = '';
      this.syncOverlayVisibility();
    }

    if (this.input.value) {
      // Don't fully stop here: this.input.value can change without ever
      // firing a real 'input' event - e.g. predictive-search.js's own
      // reset-button handler (#resetSearch) sets `searchInput.value = ''`
      // as a direct property assignment, which `handleInput` (which
      // re-enters here on every real keystroke - see connectedCallback)
      // can never see. Rescheduling a lightweight recheck (no phrase-text
      // DOM write, this branch always returns before reaching that) means
      // the animation still resumes on its own shortly after any such
      // external clear, not just after a real keystroke/blur -
      // syncOverlayVisibility() keeps the `hidden` attribute honest in the
      // meantime too, for the same reason.
      this.syncOverlayVisibility();
      this.timerId = setTimeout(() => this.tick(), 250);
      return;
    }

    // More than one instance of this element can exist on a page at once
    // (desktop header slot + mobile header row + drawer), only one of
    // which is ever visible at a given breakpoint (CSS display:none on
    // the others, or the drawer's closed <details> state). Skip the DOM
    // write while hidden, but keep the cycle alive on a short fixed delay
    // so it picks back up promptly if it becomes visible again.
    //
    // Checked on `this.input`, NOT `this` (the custom element itself):
    // the drawer wraps this element with `display: contents` (so it
    // never disturbs the existing search form's own layout) - a
    // display:contents element has no box at all, so its own
    // offsetParent is always null regardless of real visibility. The
    // <input> inside it keeps a normal box either way, so its
    // offsetParent correctly reflects whether the drawer is actually
    // open and visible.
    if (this.input.offsetParent === null) {
      this.timerId = setTimeout(() => this.tick(), 400);
      return;
    }

    const phrase = this.phrases[this.phraseIndex];
    let delay = this.deleting ? 35 : 65;

    if (!this.deleting) {
      this.charIndex += 1;
      this.overlay.textContent = this.searchPrefix + phrase.slice(0, this.charIndex);
      if (this.charIndex >= phrase.length) {
        this.deleting = true;
        delay = 1400;
      }
    } else {
      this.charIndex -= 1;
      this.overlay.textContent = this.searchPrefix + phrase.slice(0, this.charIndex);
      if (this.charIndex <= 0) {
        this.deleting = false;
        this.phraseIndex = (this.phraseIndex + 1) % this.phrases.length;
        delay = 400;
      }
    }

    this.timerId = setTimeout(() => this.tick(), delay);
  }
}

if (!customElements.get('sarky-animated-search')) {
  customElements.define('sarky-animated-search', SarkyAnimatedSearch);
}
